import { AuthResponse, LoginCredentials, UserData, TokenData } from './types';
import { ApiResponseError, NetworkError, AuthenticationError } from './errors';
import API_CONFIG from '../../config/api.config';
import { isKioskMode, KIOSK_ENDPOINTS } from './kiosk.service';

class AuthService {
  private static instance: AuthService;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private userData: UserData | null = null;
  private permissions: string[] = [];
  private tokenExpiryTime: number | null = null;
  // Lifetime of the current access token, used to scale the early-refresh
  // margin in isTokenExpired(). Defaults to the 15-minute desktop token.
  private tokenLifetimeMs: number = 900000;
  private ongoingRefreshPromise: Promise<string | null> | null = null;
  // Why the last refresh returned null. Only 'auth' — the server actually
  // answered 401 — means the session is over. Everything else is transient: a
  // frozen background tab on Android, a Wi-Fi→4G handover, a Cloudflare 5xx.
  // The distinction has to survive the null return, because the logout path it
  // feeds (POST /stoc/auth/v2/logout) deletes the 30-day refresh cookie
  // unconditionally — one failed fetch would otherwise cost a password login.
  private lastRefreshFailure: 'auth' | 'transient' | null = null;

  private constructor() {
    this.loadSavedData();
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private async loadSavedData(): Promise<void> {
    try {
      // Do not load tokens from localStorage in v2 for security. Tokens are kept in memory.
      this.accessToken = null;
      this.refreshToken = null;
      this.tokenExpiryTime = null;
      // Do not hydrate user data from storage
      this.userData = null;
      this.permissions = [];
      
    } catch (error) {
      console.error('Error loading saved authentication data:', error);
    }
  }

  private async saveAuthData(token: string, refreshToken: string, expiresIn: number, userData: UserData): Promise<void> {
    try {
      const expiryTime = Date.now() + expiresIn * 1000;
      this.lastRefreshFailure = null;

      // Do not persist any user data to storage
      try { localStorage.removeItem(API_CONFIG.STORAGE_KEYS.USER_DATA); } catch {}

      this.accessToken = token;
      this.refreshToken = refreshToken;
      this.tokenExpiryTime = expiryTime;
      this.tokenLifetimeMs = expiresIn * 1000;
      this.userData = userData || null;
      this.permissions = (userData && (userData as any).permissions) ? (userData as any).permissions : [];
      
      console.info('Saved authentication data');
    } catch (error) {
      console.error('Error saving authentication data:', error);
      throw new Error('Failed to save authentication data');
    }
  }

  public async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Prefer v2 JSON login with HttpOnly refresh cookie
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.V2_LOGIN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new ApiResponseError(
          data.message || data.detail || 'Login failed',
          response.status,
          data.details
        );
      }

      if (data.access_token) {
        const authData = {
          accessToken: data.access_token,
          refreshToken: '', // refresh is set via HttpOnly cookie
          tokenType: data.token_type,
          expiresIn: data.expires_in || 900,
          user: {
            ...data.user,
            permissions: data.user?.permissions || [],
            roles: data.user?.roles || []
          }
        };

        await this.saveAuthData(
          authData.accessToken,
          authData.refreshToken,
          authData.expiresIn,
          authData.user
        );
        return authData;
      }

      throw new AuthenticationError('Invalid response format');
    } catch (error) {
      // Fallback: keep old login for compatibility if v2 not available
      if (error instanceof ApiResponseError && error.statusCode === 404) {
        try {
          const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LOGIN}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              username: credentials.username,
              password: credentials.password
            }),
          });

          const data = await response.json();
          if (!response.ok) {
            throw new ApiResponseError(
              data.message || 'Login failed',
              response.status,
              data.details
            );
          }
          if (data.access_token) {
            const authData = {
              accessToken: data.access_token,
              refreshToken: data.refresh_token || '',
              tokenType: data.token_type,
              expiresIn: data.expires_in || 3600,
              user: {
                ...data.user,
                permissions: data.user.permissions || [],
                roles: data.user.roles || []
              }
            };
            await this.saveAuthData(
              authData.accessToken, 
              authData.refreshToken, 
              authData.expiresIn,
              authData.user
            );
            return authData;
          }
        } catch (fallbackErr) {
          if (fallbackErr instanceof ApiResponseError) throw fallbackErr;
          if (fallbackErr instanceof TypeError) throw new NetworkError('Network error occurred');
          throw new Error('An unexpected error occurred');
        }
      }
      if (error instanceof ApiResponseError) throw error;
      if (error instanceof TypeError) throw new NetworkError('Network error occurred');
      throw new Error('An unexpected error occurred');
    }
  }

  public async refreshAccessToken(): Promise<string | null> {
    // Use cookie-based refresh (no refresh token in JS)
    // De-duplicate concurrent refresh calls across requests/tabs
    if (this.ongoingRefreshPromise) {
      return this.ongoingRefreshPromise;
    }
    this.ongoingRefreshPromise = (async (): Promise<string | null> => {
      try {
        // On a public terminal there is no 30-day refresh cookie to renew from:
        // the session lives in a browser-session cookie backed by a row in
        // kiosk_sessions, which each tab exchanges for its own 60s token. Routing
        // the refresh here is what lets every existing screen work unmodified on
        // the kiosk — including apiClient's retry-on-401 path — and what makes a
        // lock take effect everywhere, since a closed session mints nothing.
        const endpoint = isKioskMode()
          ? KIOSK_ENDPOINTS.TOKEN
          : API_CONFIG.ENDPOINTS.V2_REFRESH;
        const response = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, {
          method: 'POST',
          credentials: 'include',
        });
        const data = await response.json();
        if (!response.ok) {
          throw new ApiResponseError(data.message || data.detail || 'Token refresh failed', response.status, data.details);
        }
        if (data.access_token) {
          const expiresIn = data.expires_in || 900;
          await this.saveAuthData(
            data.access_token,
            '',
            expiresIn,
            this.userData || ({} as UserData)
          );
          window.dispatchEvent(new CustomEvent('auth:tokenRefreshed', { detail: { accessToken: data.access_token } }));
          return data.access_token;
        }
        throw new AuthenticationError('Invalid response format');
      } catch (error) {
        console.error('Failed to refresh token:', error);
        // A 401 is the only answer that means the refresh cookie is spent. A
        // 5xx, a Cloudflare error page (which fails .json()) or a fetch that
        // never left the device must keep the session, so the next attempt can
        // still use the cookie.
        const isAuthFailure = error instanceof ApiResponseError && (error as any).statusCode === 401;
        this.lastRefreshFailure = isAuthFailure ? 'auth' : 'transient';
        if (isAuthFailure) {
          await this.logout();
          window.dispatchEvent(new CustomEvent('auth:sessionExpired'));
        }
        return null;
      }
    })();
    const result = await this.ongoingRefreshPromise;
    this.ongoingRefreshPromise = null;
    return result;
  }

  public async logout(): Promise<void> {
    try {
      // Call v2 logout to clear refresh cookie; ignore errors
      await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.V2_LOGOUT}`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Clear local data regardless of API call success
      localStorage.removeItem(API_CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(API_CONFIG.STORAGE_KEYS.TOKEN_EXPIRY);
      localStorage.removeItem(API_CONFIG.STORAGE_KEYS.USER_DATA);
      this.accessToken = null;
      this.refreshToken = null;
      this.tokenExpiryTime = null;
      this.userData = null;
      this.permissions = [];
    }
  }

  public isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  /**
   * Why the last refreshAccessToken() returned null, so callers can tell an
   * ended session apart from a connection that dropped. Read it right after a
   * null return; a later success resets it.
   */
  public getLastRefreshFailure(): 'auth' | 'transient' | null {
    return this.lastRefreshFailure;
  }

  public isTokenExpired(): boolean {
    if (!this.tokenExpiryTime) return true;

    // Refresh a little before the real expiry so a request never travels with a
    // token that dies in flight. The margin has to scale with the token's own
    // lifetime: it used to be a flat 60s, which is fine for the 15-minute
    // desktop token but marks a 60s kiosk token as expired the instant it is
    // minted — every single API call would then pay for a token mint first.
    const margin = Math.min(60000, Math.max(2000, this.tokenLifetimeMs * 0.25));
    return Date.now() > (this.tokenExpiryTime - margin);
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Drop the in-memory access token without ending the app's auth state.
   * Used when a kiosk terminal locks: the server-side session row is already
   * closed, and this stops a covered tab from spending the token's remaining
   * seconds. Unlike logout(), it keeps userData so the provider can tell
   * whether the next person to authorize is the same one who was working.
   */
  public clearAccessToken(): void {
    this.accessToken = null;
    this.tokenExpiryTime = null;
  }

  public getRefreshToken(): string | null {
    return this.refreshToken;
  }

  public getUserData(): UserData | null {
    return this.userData;
  }

  public hasPermission(permission: string): boolean {
    return this.permissions.includes(permission);
  }

  public getPermissions(): string[] {
    return [...this.permissions];
  }
}

export default AuthService.getInstance(); 